/**
 * Which state the Google Ads pages (GoogleAdsOpsPage) are in for the active
 * client. Pure function, no React.
 *
 * - `open`       render the section.
 * - `checking`   capabilities are still loading: mount nothing, request nothing.
 * - `no_account` the client has no Google Ads customer id: show the empty state.
 *
 * Rules, in order:
 *  1. An exempt section is always `open` (the fleet Client Dashboard and Budget
 *     Pacing work without a Google Ads account).
 *  2. Capabilities known: `customerMapped` decides (cached data wins even after
 *     a failed refetch).
 *  3. No capabilities and the fetch failed: the workspace list flag decides.
 *  4. Otherwise (loading or idle): `checking`. There is no "open by default".
 *
 * Any section not in the exempt set is gated, including ones we do not know.
 */
export type GoogleAdsGateState = 'open' | 'checking' | 'no_account';

export const GATE_EXEMPT_SECTIONS: ReadonlySet<string> = new Set([
  'client-dashboard',
  'weekly-management',
  'budgets',
]);

export function resolveGoogleAdsGate(args: {
  section: string;
  capabilities: { googleAds: { customerMapped: boolean } } | undefined;
  isLoading: boolean;
  isError: boolean;
  listFlag: boolean | undefined;
}): GoogleAdsGateState {
  // isLoading stays in the contract but is not read: no data and no error already means loading or idle.
  const { section, capabilities, isError, listFlag } = args;
  if (GATE_EXEMPT_SECTIONS.has(section)) return 'open';
  if (capabilities) return capabilities.googleAds.customerMapped ? 'open' : 'no_account';
  if (isError) return listFlag === true ? 'open' : 'no_account';
  return 'checking';
}
